// CSS Modules - component class name prefix: RotatingCarousel__

<template>
  <div
    :class="$style.container"
    :style="[
      bgImgUrl
        ? { backgroundImage: `url(${bgImgUrl})` }
        : { backgroundImage: 'none' }
    ]"
    @mouseleave="resumeAutoPlay"
    @touchstart="stopAutoPlay"
    @touchend="resumeAutoPlay"
    @mousedown="stopUserSelect"
  >
    <div
      :id="uid"
      :class="['swiper', { RotatingCarousel__singleSlide: slides.length <= 2 }]"
    >
      <div class="swiper-wrapper">
        <div
          :class="['swiper-slide', $style.slide]"
          v-for="(slide, index) in slides"
          :key="`${slide.text}_${index}`"
        >
          <CarouselSlide :slide="slide" @openModal="openModal" />
        </div>
      </div>
      <div v-if="slides.length >= 2" class="swiper-button-prev">
        <ChevronLeft />
      </div>
      <div v-if="slides.length >= 2" class="swiper-button-next">
        <ChevronRight />
      </div>
    </div>
    <div
      v-if="slides.length < 2"
      class="swiper-button-prev RotatingCarousel__singleSlide"
    >
      <ChevronLeft />
    </div>
    <div
      v-if="slides.length < 2"
      class="swiper-button-next RotatingCarousel__singleSlide"
    >
      <ChevronRight />
    </div>
    <div
      v-if="slides.length > 1 && !hidePagination"
      :class="['swiper-pagination', `swiper-pagination-${uid}`]"
    ></div>
    <Modal
      :isNotYouTube="!isYouTube"
      :isVideo="true"
      :isModalOpen="isModalOpen"
      @closeModal="closeModal"
    >
      <iframe
        v-if="isYouTube"
        :data-src="videoUrl"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      ></iframe>
      <video v-else ref="video" muted autoplay loop controls>
        <source :src="videoUrl" type="video/mp4" />
      </video>
    </Modal>
  </div>
</template>

<script>
import CarouselSlide from "./CarouselSlide";
import Modal from "../Modal/Modal";
import ChevronLeft from "../../assets/svg/ChevronLeft";
import ChevronRight from "../../assets/svg/ChevronRight";
import Swiper, {
  Navigation,
  Pagination,
  Autoplay,
  EffectCoverflow
} from "swiper";
import "swiper/swiper-bundle.min.css";

export default {
  name: "RotatingCarousel",
  components: {
    CarouselSlide,
    ChevronLeft,
    ChevronRight,
    Modal
  },
  props: {
    uid: {
      type: String
    },
    slides: {
      type: Array,
      default: () => []
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    bgImgUrl: {
      type: String
    },
    hidePagination: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isModalOpen: false,
      isYouTube: false,
      videoUrl: null
    };
  },
  methods: {
    openModal(video, isYouTube) {
      this.stopAutoPlay();
      this.videoUrl = video;
      this.isYouTube = isYouTube;
      this.isModalOpen = true;
      if (this.$refs.video && !this.isYouTube) {
        this.$refs.video.load();
      }
    },
    closeModal() {
      if (this.$refs.video) {
        this.$refs.video.pause();
      }
      this.isModalOpen = false;
      this.resumeAutoPlay();
    },
    stopAutoPlay() {
      const swiper = document.querySelector(`#${this.uid}`);
      if (swiper == null || !swiper.swiper.autoplay.running) {
        return;
      }
      swiper.swiper.autoplay.stop();
    },
    resumeAutoPlay() {
      if (!this.isModalOpen && this.autoplay && this.slides.length > 1) {
        const swiper = document.querySelector(`#${this.uid}`);
        if (swiper == null) {
          return;
        }
        swiper.swiper.autoplay.start();
      }
    },
    stopUserSelect(e) {
      e.preventDefault();
    }
  },
  mounted: function() {
    const swiper = new Swiper(`#${this.uid}`, {
      modules: [Navigation, Pagination, Autoplay, EffectCoverflow],
      centeredSlides: true,
      autoplay: {
        delay: 4000,
        disableOnInteraction: true,
        pauseOnMouseEnter: true
      },
      speed: 200,
      direction: "horizontal",
      grabCursor: true,
      pagination: {
        el: `.swiper-pagination-${this.uid}`,
        clickable: true
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev"
      },
      effect: "coverflow",
      slideToClickedSlide: true,
      spaceBetween: 40,
      slidesPerView: "auto",
      loop: true,
      loopPreventsSliding: false,
      preloadImages: true,
      coverflowEffect: {
        depth: 100,
        modifier: 1,
        rotate: 38,
        scale: 1,
        slideShadows: true,
        stretch: 0
      },
      observer: true,
      focusableElements: "link, button"
    });
    if (!this.autoplay || this.slides.length === 1) {
      swiper.autoplay.stop();
    }
    // Note: use js addEventListener and data attributen for cloned slides
    // because vue slide onClick initialization fails with swiper clones
    swiper.slides.forEach((slide) => {
      let target = slide.querySelector('button[class*="videoSlide"]');
      if (target == null) return;
      slide.addEventListener("click", () => {
        this.openModal(target.dataset.videoUrl, target.dataset.isYoutube);
      });
    });
  }
};
</script>

<style module lang="scss">
@import "./RotatingCarousel.scss";
</style>
